import { Suspense, useRef, useState } from "react";
import {
  CircularProgress,
  List,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { Form, Formik, type FormikProps, getIn } from "formik";
import { ErrorBoundary } from "react-error-boundary";
import { FleetConfigFooter } from "src/components/FleetConfigFooter";
import { FleetConfigLoadAlert } from "src/components/fleet/FleetConfigLoadAlert";
import { FleetConfigSelection } from "src/components/fleet/FleetConfigSelection";
import { PercentageVehicleRow } from "src/components/fleet/PercentageVehicleRow";
import { FormikDirtyTracker } from "src/forms/FormikDirtyTracker";
import {
  useFleetPercentageConfig,
  useFleetPercentageConfigUpdateMutation,
} from "src/hooks";
import { useFleetPercentageSchema } from "src/schemas/fleet-percentage.schema";
import type { FleetPercentageCategory, PredefinedLEZOption } from "src/types";

interface Props {
  lezId: number | string;
  scenarioId: number | string;
  onClose: () => void;
  readOnly: boolean;
  options: PredefinedLEZOption[];
  percentageFleetNameKey: string;
}

export function PercentagesFleetConfig(props: Props) {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<CircularProgress />}>
        <DataLoader {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function DataLoader(props: Props) {
  const {
    lezId,
    scenarioId,
    onClose,
    readOnly,
    percentageFleetNameKey,
    options,
  } = props;
  const [selectedOption, setSelectedOption] = useState<string>(
    percentageFleetNameKey
  );
  const { t } = useTranslation();
  const [loadedPercentageFleetNameKey, setLoadedPercentageFleetNameKey] =
    useState<string>(percentageFleetNameKey);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const formikRef =
    useRef<FormikProps<{ categories: FleetPercentageCategory[] }>>(null);
  const {
    data: fleetPercentageConfig,
    isLoading: isLoadingFleetPercentageConfig,
  } = useFleetPercentageConfig(lezId, loadedPercentageFleetNameKey);

  const {
    mutateAsync: updateFleetPercentageConfig,
    error,
    isLoading,
  } = useFleetPercentageConfigUpdateMutation(
    lezId,
    scenarioId,
    loadedPercentageFleetNameKey
  );
  const validationSchema = useFleetPercentageSchema();

  async function handleSave(categories: FleetPercentageCategory[]) {
    await updateFleetPercentageConfig(categories, {
      onSuccess: () => {
        onClose();
      },
    });
  }

  function flattenErrors(errors: unknown): string[] {
    if (typeof errors === "object" && errors !== null) {
      return Object.values(errors).flatMap((value) => flattenErrors(value));
    }
    if (Array.isArray(errors)) {
      return errors.flatMap((value) => flattenErrors(value));
    }
    return typeof errors === "string" ? [errors] : [];
  }

  const pendingFleetLoad =
    selectedOption !== "" && selectedOption !== loadedPercentageFleetNameKey;

  return (
    <>
      <FleetConfigSelection
        options={options}
        readOnly={readOnly}
        selectedOption={selectedOption}
        onSelect={setSelectedOption}
        onLoad={setLoadedPercentageFleetNameKey}
        loadedOption={loadedPercentageFleetNameKey}
        isLoading={isLoadingFleetPercentageConfig}
        formikRef={formikRef.current}
        isFormDirty={isFormDirty}
      />
      {pendingFleetLoad && (
        <FleetConfigLoadAlert
          options={options}
          selectedOption={selectedOption}
          loadedOption={loadedPercentageFleetNameKey}
          readOnly={readOnly}
          isLoading={isLoadingFleetPercentageConfig}
          onLoad={() => setLoadedPercentageFleetNameKey(selectedOption)}
        />
      )}
      {isLoadingFleetPercentageConfig && <CircularProgress />}
      {!isLoadingFleetPercentageConfig && !pendingFleetLoad && (
        <Formik
          initialValues={{ categories: fleetPercentageConfig ?? [] }}
          validationSchema={validationSchema}
          validateOnChange={true}
          validateOnBlur={false}
          validateOnMount={false}
          key={loadedPercentageFleetNameKey}
          enableReinitialize={false}
          innerRef={formikRef}
          onSubmit={(values) => handleSave(values.categories)}
        >
          {({ handleSubmit, errors, isSubmitting }) => {
            const categoryErrors = getIn(errors, "categories");

            const flattenedErrors = categoryErrors
              ? flattenErrors(categoryErrors)
              : [];

            return (
              <Styles>
                <FormikDirtyTracker
                  dirty={isFormDirty}
                  onDirtyChange={setIsFormDirty}
                />
                <div className="container">
                  <div className="header-row">
                    <Typography className="header-row-label" fontWeight="bold">
                      {t("label.vehicle")} / {t("label.fuel")} / {t("label.emission")}
                    </Typography>
                    <Typography fontWeight="bold">{t("label.percentageShare")} (%)</Typography>
                    <span>
                      {flattenedErrors.length > 0 && (
                        <Tooltip
                          title={
                            <Typography
                              color="white"
                              fontSize={12}
                              display="flex"
                              flexDirection="column"
                              gap={1}
                            >
                              {flattenedErrors.map((error) => (
                                <span key={error}>{error}</span>
                              ))}
                            </Typography>
                          }
                        >
                          <WarningAmberRounded color="error" />
                        </Tooltip>
                      )}
                    </span>
                  </div>
                  <List className="overflow-container" disablePadding>
                    {fleetPercentageConfig?.map((category, categoryIndex) => (
                      <PercentageVehicleRow
                        key={category.category}
                        category={category}
                        categoryIndex={categoryIndex}
                        readOnly={readOnly || isSubmitting}
                      />
                    ))}
                  </List>
                </div>
                <FleetConfigFooter
                  onClose={onClose}
                  onSave={() => {
                    handleSubmit();
                  }}
                  disabled={readOnly || isLoading}
                  errors={error?.response?.data}
                  isLoading={isLoading}
                />
              </Styles>
            );
          }}
        </Formik>
      )}
    </>
  );
}

const Styles = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  > .container {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    border: 1px solid ${({ theme }) => theme.palette.divider};
    box-shadow: ${(props) => props.theme.shadows[1]};
    overflow: hidden;
  }
  .header-row {
    display: grid;
    min-height: 60px;
    grid-template-columns: 30px 1fr 1fr 30px;
    gap: 16px;
    align-items: center;
    background-color: #fafafa;
    padding: 8px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
    flex-shrink: 0;
    .MuiTypography-root {
      font-weight: bold;
      font-size: 13px;
    }
    .header-row-label {
      grid-column: 1 / 3;
    }
  }
  .overflow-container {
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
    flex: 1 1 auto;
    min-height: 0;
    padding: 0;
  }
`;
